import {
  Checkbox,
  CheckboxOverrides,
  CheckboxProps,
  LABEL_PLACEMENT,
} from 'baseui/checkbox';
import { CheckboxCheckedSVG, CheckboxIndeterminateSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import * as React from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import merge from 'ts-deepmerge';

import CustomFormControl from '../custom-form-control';
import { BaseElementInputProps } from '../field-type';
import { FormContext } from '../form';

export interface CheckBoxInputProps
  extends Omit<CheckboxProps, 'name'>,
    BaseElementInputProps {
  label?: string;
  noMargin?: boolean;
  type: 'check-box';
  fieldType?: 'control' | 'uncontrol';
  onAfterChange?: (val: boolean) => void;
}

const Checkmark = {
  component: ({ $checked, $isIndeterminate, $disabled, $isFocused }) => {
    if ($isIndeterminate)
      return (
        <IndeterminateBox $disabled={$disabled} $isFocused={$isFocused}>
          <CheckboxIndeterminateSVG />
        </IndeterminateBox>
      );
    else if ($checked)
      return (
        <CheckedBox $disabled={$disabled} $isFocused={$isFocused}>
          <CheckboxCheckedSVG width={11} height={8} />
        </CheckedBox>
      );
    return <UncheckedBox $disabled={$disabled} $isFocused={$isFocused} />;
  },
};

const overrides: (props: {
  overrides?: CheckboxOverrides;
}) => CheckboxOverrides = (props) => {
  return merge(
    {
      Toggle: {
        style: ({ $checked, $theme }) => {
          return {
            backgroundColor: $checked ? $theme.colors.secondaryDark : 'white',
          };
        },
      },
      ToggleTrack: {
        style: ({ $theme }) => ({
          backgroundColor: $theme.colors.skyLighter,
        }),
      },
      Root: {
        style: {
          width: 'fit-content',
        },
      },
      Label: {
        style: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      },
      Checkmark,
    } as CheckboxOverrides,
    { ...props.overrides },
  );
};

export default function CheckBoxInput(props: CheckBoxInputProps) {
  const { control } = useFormContext<any>();
  const watchValue = useWatch({ control, name: props.name });
  const {
    field: { ref, ...field },
    fieldState,
  } = useController({ name: props.name });
  const context = React.useContext(FormContext);
  const {
    readOnly,
    noMargin = false,
    label = '',
    type,
    disabled,
    onChange,
    fieldType = 'control',
    checked,
    onAfterChange,
    ...restProps
  } = props;
  const value = fieldType === 'control' ? field.value : watchValue;

  const _isDisabled = React.useMemo(() => {
    return !context.editable || readOnly || disabled;
  }, [context.editable, disabled, readOnly]);
  const shouldShowError = !!fieldState.error?.message;

  return (
    <CustomFormControl noMargin={noMargin} error={fieldState.error?.message}>
      <Checkbox
        //@ts-ignore
        inputRef={ref}
        {...restProps}
        error={shouldShowError}
        checked={checked || value}
        disabled={_isDisabled}
        labelPlacement={LABEL_PLACEMENT.right}
        onChange={(e: any) => {
          field.onChange(e.target?.checked);
          onChange?.(e);
          onAfterChange?.(e.target?.checked);
        }}
        overrides={overrides({ overrides: props.overrides })}
      >
        {label}
      </Checkbox>
    </CustomFormControl>
  );
}

const UncheckedBox = themedStyled<
  'div',
  { $disabled: boolean; $isFocused: boolean }
>('div', ({ $theme, $disabled, $isFocused }) => ({
  width: '16px',
  height: '16px',
  minWidth: '16px',
  minHeight: '16px',
  borderWidth: $isFocused ? '2.6px' : '1.6px',
  borderColor: $disabled
    ? $theme.colors.skyLight
    : $isFocused
    ? stylesColors.primaryDefault
    : $theme.colors.inkLightest,
  borderStyle: 'solid',
  borderRadius: '2px',
  margin: '2px',
}));

const IndeterminateBox = themedStyled<
  'div',
  { $disabled: boolean; $isFocused: boolean }
>('div', ({ $theme, $disabled }) => ({
  width: '16px',
  height: '16px',
  minWidth: '16px',
  minHeight: '16px',
  borderRadius: '2px',
  background: $disabled ? $theme.colors.skyLight : $theme.colors.primaryDefault,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '2px',
}));

const CheckedBox = themedStyled<
  'div',
  { $disabled: boolean; $isFocused: boolean }
>('div', ({ $theme, $disabled }) => ({
  width: '16px',
  height: '16px',
  minWidth: '16px',
  minHeight: '16px',
  borderRadius: '2px',
  background: $disabled ? $theme.colors.skyLight : $theme.colors.primaryDefault,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '2px',
}));
